import * as PropTypes from 'prop-types';

import {Owner, OwnerInfo} from 'auth/PermissionConstants';
import {Strand} from 'constants/MolbioConstants';
import enzymes from 'data/enzymes.json';

export const EnzymePreferences = {
  PROVIDER: 'enzymes.provider',
  ENZYME_LIST_ID: 'enzymeListId',
  VIEW_ENZYMES: 'viewEnzymes',
};

export const NO_ENZYME_SENTINEL = -1;

export interface EnzymeList {
  count: number;
  description: string;
  id: number;
  members: Array<number>;
  name: string;
  created_at: string;
  owner: Owner;
  ownerInfo: OwnerInfo;
  owner_handle: string;
  owner_url: string;
  owner_url_identifier: string;
}

export interface Enzyme {
  name: string;
  id: number;
  seq: string;
  offset: number;
  offset2?: number;
  cut: number;
  cut2?: number;
}

export interface DigestableEnzyme extends EnzymeFromJSON {
  left: number;
  right: number;
  matchingCut: number;
  matchingCut2?: number;
  matchingRegion: string;
}

export interface Cut {
  position: number;
  strand: Strand;
}

export enum EnzymeType {
  SINGLE = 'single',
  DOUBLE = 'double',
  SINGLEANDDOUBLE = 'singleanddouble',
  ALL = 'all',
  NONE = 'none',
}

const NEB: BaseProvider = {
  name: 'NEB',
  buffers: ['1.1', '2.1', '3.1', '4/CS'],
  subProvider: {
    name: 'NEB HF',
    shortName: 'HF',
    infoLink: `https://www.neb.com/products/restriction-endonucleases/hf-nicking-master-mix-time-saver-other\
/high-fidelity-restriction-enzymes/high-fidelity-restriction-endonucleases`,
    buffers: ['1.1', '2.1', '3.1', '4/CS'],
  },
};

const THERMO_FISHER: BaseProvider = {
  name: 'ThermoFisher',
  buffers: ['B', 'G', 'O', 'R', 'T', '2xT'],
  subProvider: {
    name: 'ThermoFisher FastDigest',
    shortName: 'FastDigest',
    infoLink: `https://www.thermofisher.com/us/en/home/brands/thermo-scientific/molecular-biology\
/thermo-scientific-restriction-modifying-enzymes/restriction-enzymes-thermo-scientific\
/fastdigest-thermo-scientific.html`,
    buffers: ['F'],
  },
};

const PROMEGA: BaseProvider = {
  name: 'Promega',
  buffers: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'MultiCore'],
};

const SIBENZYME: BaseProvider = {
  name: 'SibEnzyme',
  buffers: ['B', 'G', 'O', 'W', 'Y', 'Rose'],
};

export interface BaseProvider {
  name: ProviderName;
  buffers: Array<string>;
  subProvider?: SubProvider;
}

interface SubProvider {
  name: ProviderName;
  buffers: Array<string>;
  shortName: string;
  infoLink: string;
}

export type Provider = BaseProvider | SubProvider;

const providerShape = {
  name: PropTypes.string.isRequired,
  buffers: PropTypes.arrayOf(PropTypes.string.isRequired),
};
const subProviderType = PropTypes.shape({
  ...providerShape,
  shortName: PropTypes.string.isRequired,
  infoLink: PropTypes.string.isRequired,
});
const baseProviderType = PropTypes.shape({...providerShape, subProvider: subProviderType});

const ALL_ENZYMES_LIST_ID = 'ALL';
const COMMERCIAL_ENZYMES_LIST_ID = -1;

export type ProviderName =
  | 'NEB'
  | 'NEB HF'
  | 'SibEnzyme'
  | 'Promega'
  | 'ThermoFisher'
  | 'ThermoFisher FastDigest'
  | 'All enzymes'
  | 'Deduplicated commercial';

export type ProviderListId = ProviderName | typeof ALL_ENZYMES_LIST_ID | typeof COMMERCIAL_ENZYMES_LIST_ID;

export interface ProvidedEnzyme {
  // TODO list out activities as enum (4/CS/ etc)
  activities: {[activity: string]: string};
  name: string;
  url: string | null;
  inactivationTemperature: number | null;
  incubationTemperature: number;
  provider: string;
}

export interface EnzymeFromJSON {
  apiIdentifier: string;
  cut: number;
  cut2?: number;
  name: string;
  seq: string;
  isoschizomers: Array<number>;
  compatible: Array<number>;
  offset: number;
  offset2?: number;
  id: number;
  providedEnzymes: {[source: string]: ProvidedEnzyme | undefined};
}

export interface ProviderList {
  id: ProviderListId;
  name: ProviderName;
  baseProviders: Array<BaseProvider>;
  defaultProvider: BaseProvider;
  dedup?: boolean;
}

const providerLists: Array<ProviderList> = [
  {
    id: ALL_ENZYMES_LIST_ID,
    name: 'All enzymes',
    baseProviders: [NEB, THERMO_FISHER, PROMEGA, SIBENZYME],
    defaultProvider: NEB,
  },
  {
    id: COMMERCIAL_ENZYMES_LIST_ID,
    name: 'Deduplicated commercial',
    baseProviders: [NEB, THERMO_FISHER, PROMEGA, SIBENZYME],
    defaultProvider: NEB,
    dedup: true,
  },
  {
    id: NEB.name,
    name: 'NEB',
    baseProviders: [NEB],
    defaultProvider: NEB,
  },
  {
    id: THERMO_FISHER.name,
    name: 'ThermoFisher',
    baseProviders: [THERMO_FISHER],
    defaultProvider: THERMO_FISHER,
  },
  {
    id: PROMEGA.name,
    name: 'Promega',
    baseProviders: [PROMEGA],
    defaultProvider: PROMEGA,
  },
  {
    id: SIBENZYME.name,
    name: 'SibEnzyme',
    baseProviders: [SIBENZYME],
    defaultProvider: SIBENZYME,
  },
];

const EnzymeConstants = {
  ALL_ENZYMES_LIST_ID,
  COMMERCIAL_ENZYMES_LIST_ID,
  DEFAULT_ENZYMES_LIST_ID: COMMERCIAL_ENZYMES_LIST_ID,
  PropTypes: {
    provider: PropTypes.oneOfType([subProviderType, baseProviderType]),
    subProvider: subProviderType,
    baseProvider: baseProviderType,
  },
  enzymes: enzymes as Array<EnzymeFromJSON>,
  baseProviders: [NEB, THERMO_FISHER, PROMEGA, SIBENZYME],
  providerLists,

  // TODO: we should derive this based on enzyme properties instead of hard-coding an array.
  // AcuI, BbsI, BfuAI, BsaI, BsmBI, BspMI, BtgZI, SapI, AarI, PaqCI
  TYPE_IIS_ENZYME_IDS: [20, 85, 105, 137, 183, 204, 273, 547, 1002, 1184],
};

export default EnzymeConstants;
